import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AuthService } from './auth.service';

// Catches Android App Links opens (any https://app.lumberlinq.com/* URL — share links,
// tally sheet/shipment views, etc. — tapped from outside the app: browser, WhatsApp, SMS,
// email) and routes them into the existing Angular router, exactly as if the same URL had
// been opened in a normal browser tab. Deliberately does NOT do its own auth gating — routes
// that need it are already guarded (AuthSessionGuard), and /s/* share links already branch on tier
// (PUBLIC vs PROTECTED/PRIVATE) inside ShareDispatcherComponent. Duplicating that here would
// risk forcing a login wall onto PUBLIC share links, which only require a Turnstile check.
//
// init() is called once from AppComponent's constructor (same pattern as
// MobileOAuthCallbackService there), and is a no-op on web/non-native. The
// lumberlinq://oauth/callback deep link stays owned by MobileOAuthCallbackService — this
// service ignores it.
@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly authService = inject(AuthService);

  private static readonly OAUTH_CALLBACK_PREFIX = 'lumberlinq://oauth/callback';
  private static readonly VERIFY_EMAIL_PREFIX = 'lumberlinq://verify-email';
  private static readonly APP_LINK_HOST = 'app.lumberlinq.com';

  init(): void {
    if (!Capacitor.isNativePlatform()) return;

    // Cold start — app launched directly via the link.
    App.getLaunchUrl()
      .then((result) => {
        if (result?.url) {
          this.ngZone.run(() => this.handleUrl(result.url));
        }
      })
      .catch((err) => this.logger.debug('[DeepLink] getLaunchUrl failed', err));

    // Warm start — app already running/backgrounded, re-opened via the link.
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.ngZone.run(() => this.handleUrl(event?.url));
    });
  }

  private handleUrl(raw: string | undefined): void {
    if (!raw || raw.startsWith(DeepLinkService.OAUTH_CALLBACK_PREFIX)) return;

    // iOS-only "Verify My Email" deep link (see BrevoEmailServiceImpl.sendEmailVerificationEmail)
    // — verifies in-app immediately, never opening a browser. Safe to auto-verify without a
    // confirm-click step here (unlike the web link): a custom scheme like this isn't a real
    // HTTP(S) resource, so no email safety-scanner can pre-fetch and burn it before a genuine tap.
    if (raw.startsWith(DeepLinkService.VERIFY_EMAIL_PREFIX)) {
      this.handleVerifyEmailDeepLink(raw);
      return;
    }

    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      this.logger.warn('[DeepLink] Could not parse URL', raw);
      return;
    }

    if (url.hostname !== DeepLinkService.APP_LINK_HOST) return;

    const target = `${url.pathname}${url.search}${url.hash}`;
    this.navigateWhenReady(target);
  }

  // Cold start race (found 2026-09-27, iPhone, logged out): the link can arrive before the router's
  // own first navigation ("" -> /login) has run, and that first navigation then wins — the user is
  // left on the login page instead of the shared link. So hold the link until the first navigation
  // has settled, then go there, replacing the login page in history.
  private pendingTarget: string | null = null;
  private waitingForFirstNav = false;

  private navigateWhenReady(target: string): void {
    if (this.router.navigated) {
      this.logger.info('[DeepLink] Navigating to', target);
      this.router.navigateByUrl(target, { replaceUrl: true });
      return;
    }
    // getLaunchUrl and appUrlOpen can both report the same cold-start link — keep one.
    this.pendingTarget = target;
    if (this.waitingForFirstNav) return;
    this.waitingForFirstNav = true;
    this.logger.info('[DeepLink] Holding link until first navigation settles', target);
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
        take(1),
      )
      .subscribe(() => {
        this.waitingForFirstNav = false;
        const held = this.pendingTarget;
        this.pendingTarget = null;
        if (held) {
          this.logger.info('[DeepLink] Navigating to held link', held);
          this.ngZone.run(() => this.router.navigateByUrl(held, { replaceUrl: true }));
        }
      });
  }

  private handleVerifyEmailDeepLink(raw: string): void {
    // iOS is documented (see MobileOAuthCallbackService) to sometimes append a stray '#' to
    // custom-scheme deep links — same defensive stripping applied here.
    const clean = raw.split('#')[0];
    const queryIndex = clean.indexOf('?');
    const token = queryIndex >= 0 ? new URLSearchParams(clean.substring(queryIndex + 1)).get('token') : null;
    if (!token) {
      this.logger.warn('[DeepLink] verify-email link missing token', raw);
      return;
    }
    this.logger.info('[DeepLink] Verifying email in-app via deep link');
    this.authService.verifyEmail(token).subscribe({
      next: () => this.router.navigate(['/email-verification'], { queryParams: { success: 'true' } }),
      error: () => this.router.navigate(['/email-verification'], { queryParams: { error: 'Invalid or expired token' } }),
    });
  }
}
